import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowLeft, ArrowUp, Check, ChevronDown, Plus, Save, ShieldCheck, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../api/client";
import type { ConsumerChannelLimitView, DownstreamModelOption, DownstreamRouteOption, Policy } from "../../api/types";
import { AuditLine, DataState, PageHeader, StatusBadge } from "../../components/ui";

interface StepDraft {
  stepId: number;
  clientKey: string;
  modelId: number;
  targets: TargetDraft[];
}

interface TargetDraft {
  type: "channel" | "pool";
  id: number;
}

interface PolicyDraft {
  steps: StepDraft[];
  status: "enabled" | "disabled";
}

interface ChannelLimitDraft {
  qpsLimit: string;
  maxConcurrency: string;
  expectedUpdatedAt: number;
}

// ConsumerModelsPage configures direct model grants and their exact execution chains.
// 业务方模型页配置可调用模型及完整的模型、渠道执行顺序。
export function ConsumerModelsPage() {
  const consumerId = Number(useParams().consumerId);
  const queryClient = useQueryClient();
  const consumer = useQuery({ queryKey: ["consumer", consumerId], queryFn: () => api.consumer(consumerId) });
  const models = useQuery({ queryKey: ["downstream-options"], queryFn: api.downstreamOptions });
  const channelLimits = useQuery({ queryKey: ["consumer-channel-limits", consumerId], queryFn: () => api.consumerChannelLimits(consumerId) });
  const item = consumer.data?.data;
  const allModels = models.data?.data || [];
  const allChannelLimits = channelLimits.data?.data || [];
  const [selectedModelId, setSelectedModelId] = useState(0);
  const selectedModel = allModels.find((model) => model.id === selectedModelId);
  const currentPolicy = item?.policies.find((policy) => policy.model_id === selectedModelId);
  const [draft, setDraft] = useState<PolicyDraft>({ steps: [], status: "enabled" });
  const [savedMessage, setSavedMessage] = useState("");
  const [pendingModelId, setPendingModelId] = useState(0);
  const [modelScope, setModelScope] = useState<"configured" | "image" | "video" | "all">("configured");
  const [limitDrafts, setLimitDrafts] = useState<Record<number, ChannelLimitDraft>>({});

  useEffect(() => {
    if (!selectedModelId && item && allModels.length) {
      const configuredModelId = item.policies.find((policy) => policy.status === "enabled")?.model_id
        ?? item.policies[0]?.model_id;
      const initialModel = allModels.find((model) => model.id === configuredModelId) ?? allModels[0];
      const initialPolicy = item.policies.find((policy) => policy.model_id === initialModel.id);
      setSelectedModelId(initialModel.id);
      setDraft(policyDraft(initialPolicy, initialModel));
      if (!item.policies.length) setModelScope("all");
    }
  }, [allModels, item, selectedModelId]);

  useEffect(() => {
    setDraft(policyDraft(currentPolicy, selectedModel));
  }, [currentPolicy, selectedModel]);

  useEffect(() => {
    setLimitDrafts(channelLimitDrafts(allChannelLimits));
  }, [channelLimits.data]);

  const save = useMutation({
    mutationFn: (modelId: number) => api.savePolicy(consumerId, modelId, {
      expected_policy_updated_at: currentPolicy?.updated_at || 0,
      status: draft.status,
      steps: draft.steps.map((step) => ({
        ...(step.stepId ? { step_id: step.stepId } : {}),
        model_id: step.modelId,
        channel_ids: expandedChannelIds(step, allModels.find((model) => model.id === step.modelId)),
        targets: step.targets.map((target) => ({ target_type: target.type, target_id: target.id })),
      })),
      channel_limits: draft.status === "enabled"
        ? selectedChannelIds(draft, allModels).map((channelId) => {
          const limit = limitDrafts[channelId];
          return {
            channel_id: channelId,
            qps_limit: limit?.qpsLimit ? Number(limit.qpsLimit) : 1,
            max_concurrency: limit?.maxConcurrency ? Number(limit.maxConcurrency) : 1,
            expected_updated_at: limit?.expectedUpdatedAt || undefined,
          };
        })
        : undefined,
    }),
    onSuccess: async (result) => {
      setSavedMessage(result.refresh_pending ? "策略已保存，配置刷新等待中" : "策略已保存并发布");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["consumer", consumerId] }),
        queryClient.invalidateQueries({ queryKey: ["consumers"] }),
        queryClient.invalidateQueries({ queryKey: ["consumer-channel-limits", consumerId] }),
      ]);
    },
  });

  const visibleModels = useMemo(() => allModels.filter((model) => {
    if (modelScope === "all") return true;
    if (modelScope === "image" || modelScope === "video") return model.modality === modelScope;
    return item?.policies.some((policy) => policy.model_id === model.id);
  }), [allModels, item?.policies, modelScope]);
  const incompleteStepIndex = draft.steps.findIndex((step) => step.targets.length === 0);
  const eligibleStepModels = incompleteStepIndex >= 0 ? [] : allModels.filter((model) => model.modality === selectedModel?.modality
    && model.status === "enabled"
    && availablePhysicalChannelsForModel(draft, model).length);
  const addStepMessage = incompleteStepIndex >= 0
    ? `请先为步骤 ${incompleteStepIndex + 1} 配置渠道`
    : eligibleStepModels.length ? "选择模型并添加步骤" : "没有可继续配置的模型渠道";
  const invalidSteps = invalidStepCount(draft, allModels);
  const duplicateRouteError = repeatedModelChannelError(draft, allModels);
  const selectedLimitError = draft.status === "enabled" ? channelLimitError(draft, allModels, limitDrafts, allChannelLimits) : "";
  const path = executionPath(draft, allModels);
  const isNewPolicy = Boolean(selectedModel && !currentPolicy);
  const configuredChannelIds = selectedChannelIds(draft, allModels);
  const missingSelectedLimits = draft.status === "enabled" && configuredChannelIds.some((channelId) =>
    !allChannelLimits.find((view) => view.channel_id === channelId)?.limit);
  const limitDirty = JSON.stringify(selectedLimitValues(configuredChannelIds, limitDrafts))
    !== JSON.stringify(selectedLimitValues(configuredChannelIds, channelLimitDrafts(allChannelLimits)));
  const dirty = useMemo(
    () => isNewPolicy || missingSelectedLimits || limitDirty
      || JSON.stringify(draft) !== JSON.stringify(policyDraft(currentPolicy, selectedModel)),
    [currentPolicy, draft, isNewPolicy, limitDirty, missingSelectedLimits, selectedModel],
  );

  const selectModel = (modelId: number) => {
    if (modelId === selectedModelId) return;
    if (dirty) {
      setPendingModelId(modelId);
      return;
    }
    setSelectedModelId(modelId);
    setSavedMessage("");
  };

  const updateStep = (stepIndex: number, updater: (step: StepDraft) => StepDraft) => {
    setDraft((value) => ({ ...value, steps: value.steps.map((step, index) => index === stepIndex ? updater(step) : step) }));
  };

  const updateLimit = (channelId: number, field: "qpsLimit" | "maxConcurrency", value: string) => {
    const existing = allChannelLimits.find((view) => view.channel_id === channelId)?.limit;
    setLimitDrafts((drafts) => ({
      ...drafts,
      [channelId]: {
        qpsLimit: drafts[channelId]?.qpsLimit ?? "",
        maxConcurrency: drafts[channelId]?.maxConcurrency ?? "",
        expectedUpdatedAt: drafts[channelId]?.expectedUpdatedAt ?? existing?.updated_at ?? 0,
        [field]: value,
      },
    }));
  };

  const moveStep = (index: number, delta: number) => {
    setDraft((value) => ({ ...value, steps: move(value.steps, index, delta, 0) }));
  };

  return (
    <DataState loading={consumer.isLoading || models.isLoading || channelLimits.isLoading} error={consumer.error || models.error || channelLimits.error} empty={!item}>
      {item ? <>
        <Link to={`/manage/consumers/${consumerId}`} className="back-link"><ArrowLeft size={16} />{item.display_name}</Link>
        <PageHeader title="可用模型" description={`配置 ${item.display_name} 可以请求的模型，以及每个模型的渠道和降级顺序。`} actions={<StatusBadge value={item.status} />} />
        <AuditLine createdBy={item.created_by} createdAt={item.created_at} updatedBy={item.updated_by} updatedAt={item.updated_at} />
        <div className="policy-layout">
          <section className="content-section policy-list-section">
            <div className="section-heading">
              <div><h2>模型列表</h2><p>已配置表示业务方可以直接请求；降级模型不需要单独授权。</p></div>
              <button type="button" className="secondary-button compact" onClick={() => setModelScope("all")}><Plus size={15} />添加模型</button>
            </div>
            <div className="policy-model-filter">
              <select aria-label="模型筛选" value={modelScope} onChange={(event) => setModelScope(event.target.value as typeof modelScope)}>
                <option value="configured">已配置</option>
                <option value="image">图片</option>
                <option value="video">视频</option>
                <option value="all">全部</option>
              </select>
            </div>
            <div className="model-policy-list">
              {visibleModels.map((model) => {
                const policy = item.policies.find((candidate) => candidate.model_id === model.id);
                const routable = policy ? invalidStepCount(policyDraft(policy, model), allModels) === 0 : false;
                return <button type="button" key={model.id} aria-pressed={selectedModelId === model.id} className={selectedModelId === model.id ? "active" : ""} onClick={() => selectModel(model.id)}>
                  <div><strong>{model.display_name}</strong><span>{model.model_key}</span></div>
                  {policy ? <><StatusBadge value={policy.status === "enabled" && !routable ? "unroutable" : policy.status} label={policy.status === "enabled" && !routable ? "不可调度" : undefined} /><ChevronDown size={15} /></> : <span className="not-configured">未配置</span>}
                </button>;
              })}
            </div>
          </section>

          <section className="content-section policy-editor">
            <div className="section-heading">
              <div><h2>{selectedModel?.display_name || "选择模型"}</h2><p>{selectedModel ? `请求模型：${selectedModel.display_name}。实际按下面的模型和渠道顺序执行，可以直接使用其他同类模型。` : "从左侧选择一个请求模型。"}</p></div>
              {currentPolicy ? <StatusBadge value={draft.status === "enabled" && invalidSteps ? "unroutable" : draft.status} label={draft.status === "enabled" && invalidSteps ? "不可调度" : undefined} /> : null}
            </div>
            <div className="toggle-row">
              <div><strong>允许调用此模型</strong><span>关闭后，这个业务方不能直接请求该标准模型。</span></div>
              <button type="button" className={`switch ${draft.status === "enabled" ? "on" : ""}`} role="switch" aria-label="允许调用此模型" aria-checked={draft.status === "enabled"} onClick={() => setDraft((value) => ({ ...value, status: value.status === "enabled" ? "disabled" : "enabled" }))}><span /></button>
            </div>

            <div className="execution-chain-editor">
              <div className="chain-heading"><div><strong>执行顺序与渠道额度</strong><span>按顺序尝试渠道；额度由业务方和渠道共享，不填默认 1 QPS / 1 并发。</span></div></div>
              {draft.steps.map((step, stepIndex) => {
                const model = allModels.find((candidate) => candidate.id === step.modelId);
                const availableChannels = model ? uniqueEnabledChannels(model) : [];
                const availablePools = (model?.pools || []).filter((pool) => pool.status === "enabled");
                const selectedTargetSet = new Set(step.targets.map((target) => `${target.type}:${target.id}`));
                const selectedPhysicalChannels = new Set(expandedChannelIds(step, model));
                const usedByOtherSteps = usedPhysicalChannelsForModel(draft, step.modelId, step.clientKey, allModels);
                const unavailableForStep = new Set([...selectedPhysicalChannels, ...usedByOtherSteps.keys()]);
                const addableChannels = availableChannels.filter((channel) => !unavailableForStep.has(channel.channel_id));
                const addablePools = availablePools.filter((pool) => !selectedTargetSet.has(`pool:${pool.pool_id}`) && pool.member_channel_ids.every((channelId) => !unavailableForStep.has(channelId)));
                return <div className="chain-step" key={step.clientKey}>
                  <div className="chain-step-header">
                    <span className="chain-order">{stepIndex + 1}</span>
                    <div className="chain-step-model"><select aria-label={`步骤 ${stepIndex + 1} 模型`} value={step.modelId} onChange={(event) => {
                        const nextModel = allModels.find((candidate) => candidate.id === Number(event.target.value));
                        if (nextModel) updateStep(stepIndex, (value) => ({ ...value, modelId: nextModel.id, targets: [] }));
                      }}>{allModels.filter((candidate) => candidate.modality === selectedModel?.modality && candidate.status === "enabled").map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.display_name}</option>)}</select>
                      <span>{`执行步骤 ${stepIndex + 1}`}</span></div>
                    <div className="chain-step-actions">
                      <button type="button" className="icon-button" aria-label="上移执行步骤" disabled={stepIndex === 0} onClick={() => moveStep(stepIndex, -1)}><ArrowUp size={15} /></button>
                      <button type="button" className="icon-button" aria-label="下移执行步骤" disabled={stepIndex === draft.steps.length - 1} onClick={() => moveStep(stepIndex, 1)}><ArrowDown size={15} /></button>
                      <button type="button" className="icon-button danger-text" aria-label="移除执行步骤" onClick={() => setDraft((value) => ({ ...value, steps: value.steps.filter((_, index) => index !== stepIndex) }))}><Trash2 size={15} /></button>
                    </div>
                  </div>
                  <div className="channel-order-list">
                    {step.targets.map((target, targetIndex) => {
                      const channel = target.type === "channel" ? availableChannels.find((candidate) => candidate.channel_id === target.id)
                        ?? model?.routes.find((candidate) => candidate.channel_id === target.id) : undefined;
                      const pool = target.type === "pool" ? (model?.pools || []).find((candidate) => candidate.pool_id === target.id) : undefined;
                      const unavailable = target.type === "channel" ? !availableChannels.some((candidate) => candidate.channel_id === target.id) : !availablePools.some((candidate) => candidate.pool_id === target.id);
                      const repeated = expandedTargetChannelIds(target, model).some((channelId) => usedByOtherSteps.has(channelId));
                      const label = target.type === "pool" ? pool?.pool_name || `Pool #${target.id}` : channel?.channel_name || `渠道 #${target.id}`;
                      return <div className={`channel-order-row ${unavailable || repeated ? "invalid" : ""}`} key={`${target.type}:${target.id}`}>
                        <span>{targetIndex + 1}</span>
                        <div><strong>{label}{unavailable ? "（不可用）" : repeated ? "（已在同模型其他步骤使用）" : ""}</strong><small>{target.type === "pool" ? `${pool?.provider_key || "Pool 已移除"} · ${pool?.member_channel_ids.length || 0} 个独立成员` : channel?.provider_key || "映射已移除"}</small></div>
                        {target.type === "channel" ? <div className="channel-limit-fields">
                          <label><span>QPS</span><input type="number" min="1" step="1" placeholder="默认 1" aria-label={`${label} QPS 额度`} value={limitDrafts[target.id]?.qpsLimit ?? ""} onChange={(event) => updateLimit(target.id, "qpsLimit", event.target.value)} /></label>
                          <label><span>并发</span><input type="number" min="1" step="1" placeholder="默认 1" aria-label={`${label} 并发额度`} value={limitDrafts[target.id]?.maxConcurrency ?? ""} onChange={(event) => updateLimit(target.id, "maxConcurrency", event.target.value)} /></label>
                        </div> : <div className="channel-limit-fields"><small>成员额度由服务端按物理 Channel 物化；Pool 不创建第二层容量。</small></div>}
                        <div className="channel-order-actions">
                          <button type="button" className="icon-button" aria-label={target.type === "pool" ? "上移 Pool" : "上移渠道"} disabled={targetIndex === 0} onClick={() => updateStep(stepIndex, (value) => ({ ...value, targets: move(value.targets, targetIndex, -1) }))}><ArrowUp size={15} /></button>
                          <button type="button" className="icon-button" aria-label={target.type === "pool" ? "下移 Pool" : "下移渠道"} disabled={targetIndex === step.targets.length - 1} onClick={() => updateStep(stepIndex, (value) => ({ ...value, targets: move(value.targets, targetIndex, 1) }))}><ArrowDown size={15} /></button>
                          <button type="button" className="text-button danger-text" onClick={() => updateStep(stepIndex, (value) => ({ ...value, targets: value.targets.filter((candidate) => candidate !== target) }))}>移除</button>
                        </div>
                      </div>;
                    })}
                    <select aria-label={`${model?.display_name || step.modelId} 添加渠道或 Pool`} value="" onChange={(event) => {
                      const [type, rawID] = event.target.value.split(":");
                      const id = Number(rawID);
                      if (id && (type === "channel" || type === "pool")) updateStep(stepIndex, (value) => ({ ...value, targets: [...value.targets, { type, id }] }));
                    }}>
                      <option value="">{addableChannels.length || addablePools.length ? "添加渠道或 Pool" : "没有更多可用目标"}</option>
                      {addableChannels.map((channel) => <option key={`channel:${channel.channel_id}`} value={`channel:${channel.channel_id}`}>{channel.channel_name} · {channel.provider_key}</option>)}
                      {addablePools.map((pool) => <option key={`pool:${pool.pool_id}`} value={`pool:${pool.pool_id}`}>{pool.pool_name} · Pool · {pool.member_channel_ids.length} 成员</option>)}
                    </select>
                  </div>
                </div>;
              })}

              <div className="add-routing-step">
                <div><strong>添加执行步骤</strong><span>{incompleteStepIndex >= 0 ? `步骤 ${incompleteStepIndex + 1} 尚未配置渠道，完成后才能继续添加。` : "选择模型后，再配置该步骤使用的渠道。同一模型可重复，但渠道不能重复。"}</span></div>
                <select aria-label="添加执行步骤" value="" onChange={(event) => {
                  const model = allModels.find((candidate) => candidate.id === Number(event.target.value));
                  if (model) setDraft((value) => ({ ...value, steps: [...value.steps, emptyStep(model)] }));
                }} disabled={incompleteStepIndex >= 0 || !eligibleStepModels.length}>
                  <option value="">{addStepMessage}</option>
                  {eligibleStepModels.map((model) => <option key={model.id} value={model.id}>{model.display_name}</option>)}
                </select>
              </div>
            </div>

            {invalidSteps ? <div className="inline-error">有 {invalidSteps} 个模型步骤缺少可用渠道或包含失效配置，请修复后保存。</div> : null}
            {duplicateRouteError ? <div className="inline-error">{duplicateRouteError}</div> : null}
            {selectedLimitError ? <div className="inline-error">{selectedLimitError}</div> : null}
            {!selectedLimitError && missingSelectedLimits ? <div className="inline-note">有渠道尚未设置额度；本次保存会自动补为 1 QPS / 1 并发。</div> : null}
            <div className="path-preview">
              <div className="preview-title"><ShieldCheck size={17} /><strong>实际执行路径预览</strong></div>
              {path.length ? path.map((entry, index) => <div className="path-node" key={`${entry.stepKey}-${entry.target.type}-${entry.target.id}`}>
                <span>{index + 1}</span>
                <div><strong>{entry.model.display_name} / {entry.label}</strong><small>步骤 {entry.stepIndex + 1} · {entry.providerKey}{entry.target.type === "pool" ? " · least_utilized Pool" : ""}</small></div>
                <Check size={15} />
              </div>) : <div className="inline-error">当前策略没有可执行的上游路径</div>}
            </div>

            {savedMessage ? <div className="inline-success">{savedMessage}</div> : null}
            {save.isError ? <div className="inline-error">{save.error.message}</div> : null}
            <div className="editor-actions">
              <span className="edit-state">{isNewPolicy ? "尚未保存策略" : dirty ? "有未保存修改" : "策略已是最新"}</span>
              <button type="button" className="secondary-button" disabled={!dirty || isNewPolicy} onClick={() => { setDraft(policyDraft(currentPolicy, selectedModel)); setLimitDrafts(channelLimitDrafts(allChannelLimits)); }}>重置</button>
              <button type="button" className="primary-button" disabled={!dirty || save.isPending || !selectedModel || !draft.steps.length || Boolean(selectedLimitError) || Boolean(duplicateRouteError) || (draft.status === "enabled" && invalidSteps > 0)} onClick={() => save.mutate(selectedModelId)}><Save size={16} />{save.isPending ? "保存中" : "保存策略"}</button>
            </div>
          </section>
        </div>
        {pendingModelId ? <div className="dialog-backdrop"><div className="dialog" role="dialog" aria-modal="true"><div className="dialog-header"><div><h2>切换模型？</h2><p>当前策略有未保存修改，切换后这些修改会丢失。</p></div><button type="button" className="icon-button" onClick={() => setPendingModelId(0)} aria-label="关闭"><X size={18} /></button></div><div className="dialog-actions"><button type="button" className="secondary-button" onClick={() => setPendingModelId(0)}>继续编辑</button><button type="button" className="danger-button" onClick={() => { setSelectedModelId(pendingModelId); setLimitDrafts(channelLimitDrafts(allChannelLimits)); setPendingModelId(0); setSavedMessage(""); }}>放弃修改并切换</button></div></div></div> : null}
      </> : null}
    </DataState>
  );
}

function policyDraft(policy?: Policy, rootModel?: DownstreamModelOption): PolicyDraft {
  if (policy) {
    const steps = policy.steps?.length
      ? policy.steps.map((step, stepIndex) => ({
        stepId: step.step_id || 0,
        clientKey: step.step_id ? `saved:${step.step_id}` : `legacy:${step.model_id}:${stepIndex}`,
        modelId: step.model_id,
        targets: step.targets?.length
          ? step.targets.map((target) => ({ type: target.target_type, id: target.target_id }))
          : step.channel_ids.map((channelId) => ({ type: "channel" as const, id: channelId })),
      }))
      : rootModel ? [defaultStep(rootModel)] : [];
    return { status: policy.status, steps };
  }
  return { status: "enabled", steps: rootModel ? [defaultStep(rootModel)] : [] };
}

function defaultStep(model: DownstreamModelOption): StepDraft {
  return { stepId: 0, clientKey: `root:${model.id}`, modelId: model.id, targets: uniqueEnabledChannels(model).map((channel) => ({ type: "channel", id: channel.channel_id })) };
}

let stepDraftSequence = 0;

function emptyStep(model: DownstreamModelOption): StepDraft {
  stepDraftSequence += 1;
  return { stepId: 0, clientKey: `new:${stepDraftSequence}`, modelId: model.id, targets: [] };
}

function uniqueEnabledChannels(model: DownstreamModelOption): DownstreamRouteOption[] {
  const byId = new Map<number, DownstreamRouteOption>();
  [...model.routes]
    .filter((route) => route.status === "enabled")
    .sort((left, right) => left.route_order - right.route_order)
    .forEach((route) => {
      if (!byId.has(route.channel_id)) byId.set(route.channel_id, route);
    });
  return [...byId.values()];
}

function invalidStepCount(draft: PolicyDraft, models: DownstreamModelOption[]) {
  if (!draft.steps.length) return 1;
  const seenByModel = new Map<number, Set<number>>();
  return draft.steps.reduce((count, step) => {
    const model = models.find((candidate) => candidate.id === step.modelId);
    const availableChannels = new Set(model ? uniqueEnabledChannels(model).map((channel) => channel.channel_id) : []);
    const availablePools = new Set((model?.pools || []).filter((pool) => pool.status === "enabled").map((pool) => pool.pool_id));
    const targetKeys = step.targets.map((target) => `${target.type}:${target.id}`);
    const expanded = expandedChannelIds(step, model);
    const previouslyUsed = seenByModel.get(step.modelId) || new Set<number>();
    const repeatsPreviousStep = expanded.some((channelId) => previouslyUsed.has(channelId));
    const invalid = !model || !step.targets.length
      || new Set(targetKeys).size !== targetKeys.length
      || new Set(expanded).size !== expanded.length
      || repeatsPreviousStep
      || step.targets.some((target) => target.type === "channel" ? !availableChannels.has(target.id) : !availablePools.has(target.id));
    expanded.forEach((channelId) => previouslyUsed.add(channelId));
    seenByModel.set(step.modelId, previouslyUsed);
    return count + Number(invalid);
  }, 0);
}

function repeatedModelChannelError(draft: PolicyDraft, models: DownstreamModelOption[]) {
  const seenByModel = new Map<number, Map<number, number>>();
  for (let stepIndex = 0; stepIndex < draft.steps.length; stepIndex += 1) {
    const step = draft.steps[stepIndex];
    const model = models.find((candidate) => candidate.id === step.modelId);
    const used = seenByModel.get(step.modelId) || new Map<number, number>();
    for (const channelId of expandedChannelIds(step, model)) {
      const previousStep = used.get(channelId);
      if (previousStep !== undefined) {
        const channel = model?.routes.find((candidate) => candidate.channel_id === channelId);
        return `${model?.display_name || `模型 #${step.modelId}`} 的 ${channel?.channel_name || `渠道 #${channelId}`} 已在步骤 ${previousStep + 1} 使用；同一模型的渠道只能配置一次。`;
      }
      used.set(channelId, stepIndex);
    }
    seenByModel.set(step.modelId, used);
  }
  return "";
}

function executionPath(draft: PolicyDraft, models: DownstreamModelOption[]) {
  if (draft.status !== "enabled") return [];
  return draft.steps.flatMap((step, stepIndex) => {
    const model = models.find((candidate) => candidate.id === step.modelId);
    if (!model) return [];
    const channels = uniqueEnabledChannels(model);
    return step.targets.flatMap((target) => {
      if (target.type === "channel") {
        const channel = channels.find((candidate) => candidate.channel_id === target.id);
        return channel ? [{ model, target, label: channel.channel_name, providerKey: channel.provider_key, stepIndex, stepKey: step.clientKey }] : [];
      }
      const pool = (model.pools || []).find((candidate) => candidate.pool_id === target.id && candidate.status === "enabled");
      return pool ? [{ model, target, label: pool.pool_name, providerKey: pool.provider_key, stepIndex, stepKey: step.clientKey }] : [];
    });
  });
}

function channelLimitDrafts(views: ConsumerChannelLimitView[]): Record<number, ChannelLimitDraft> {
  return Object.fromEntries(views.flatMap((view) => view.limit ? [[view.channel_id, {
    qpsLimit: String(view.limit.qps_limit),
    maxConcurrency: String(view.limit.max_concurrency),
    expectedUpdatedAt: view.limit.updated_at,
  } satisfies ChannelLimitDraft]] : []));
}

function expandedChannelIds(step: StepDraft, model?: DownstreamModelOption) {
  return step.targets.flatMap((target) => target.type === "channel"
    ? [target.id]
    : (model?.pools || []).find((pool) => pool.pool_id === target.id)?.member_channel_ids || []);
}

function expandedTargetChannelIds(target: TargetDraft, model?: DownstreamModelOption) {
  return target.type === "channel"
    ? [target.id]
    : (model?.pools || []).find((pool) => pool.pool_id === target.id)?.member_channel_ids || [];
}

function usedPhysicalChannelsForModel(draft: PolicyDraft, modelId: number, exceptClientKey: string, models: DownstreamModelOption[]) {
  const used = new Map<number, number>();
  draft.steps.forEach((step, stepIndex) => {
    if (step.modelId !== modelId || step.clientKey === exceptClientKey) return;
    const model = models.find((candidate) => candidate.id === modelId);
    expandedChannelIds(step, model).forEach((channelId) => used.set(channelId, stepIndex));
  });
  return used;
}

function availablePhysicalChannelsForModel(draft: PolicyDraft, model: DownstreamModelOption) {
  const used = usedPhysicalChannelsForModel(draft, model.id, "", [model]);
  return uniqueEnabledChannels(model).filter((channel) => !used.has(channel.channel_id));
}

function selectedChannelIds(draft: PolicyDraft, models: DownstreamModelOption[]) {
  return [...new Set(draft.steps.flatMap((step) => expandedChannelIds(step, models.find((model) => model.id === step.modelId))))];
}

function selectedLimitValues(channelIds: number[], drafts: Record<number, ChannelLimitDraft>) {
  return channelIds.map((channelId) => ({
    channelId,
    qpsLimit: drafts[channelId]?.qpsLimit ?? "",
    maxConcurrency: drafts[channelId]?.maxConcurrency ?? "",
    expectedUpdatedAt: drafts[channelId]?.expectedUpdatedAt ?? 0,
  }));
}

function channelLimitError(draft: PolicyDraft, models: DownstreamModelOption[], drafts: Record<number, ChannelLimitDraft>, views: ConsumerChannelLimitView[]) {
  for (const channelId of selectedChannelIds(draft, models)) {
    const view = views.find((candidate) => candidate.channel_id === channelId);
    if (!view) return `渠道 #${channelId} 的额度信息读取失败，请刷新后重试。`;
    const qpsValue = drafts[channelId]?.qpsLimit ?? "";
    const concurrencyValue = drafts[channelId]?.maxConcurrency ?? "";
    if (!qpsValue && !concurrencyValue) continue;
    if (!qpsValue || !concurrencyValue) return `${view.channel_name} 的 QPS 和并发需要同时填写，或全部留空使用默认值。`;
    const qps = Number(qpsValue);
    const concurrency = Number(concurrencyValue);
    if (!Number.isInteger(qps) || !Number.isInteger(concurrency) || qps <= 0 || concurrency <= 0) {
      return `${view.channel_name} 的 QPS 和并发必须是正整数。`;
    }
    if (qps > view.channel_qps_limit || concurrency > view.channel_max_concurrency) {
      return `${view.channel_name} 的额度不能超过渠道上限（${view.channel_qps_limit} QPS / ${view.channel_max_concurrency} 并发）。`;
    }
  }
  return "";
}

function move<T>(items: T[], index: number, delta: number, minimum = 0) {
  const target = index + delta;
  if (target < minimum || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}
